import { useEffect } from 'react';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2, User as UserIcon, Mail, Shield, CheckCircle } from 'lucide-react';
import { useGetUsersMeQuery, usePatchUsersMeMutation } from '@/services';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { toast } from '@/hooks/use-toast';

const formSchema = z.object({
  first_name: z.string().min(1, 'First name is required').max(50, 'First name must be less than 50 characters'),
  last_name: z.string().min(1, 'Last name is required').max(50, 'Last name must be less than 50 characters'),
});

type FormValues = z.infer<typeof formSchema>;

export function UserSettings() {
  const { data: userData, isLoading, refetch } = useGetUsersMeQuery();
  const [updateUser, { isLoading: isUpdating }] = usePatchUsersMeMutation();

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      first_name: '',
      last_name: '',
    },
  });

  useEffect(() => {
    if (userData?.data) {
      form.reset({
        first_name: userData.data.first_name || '',
        last_name: userData.data.last_name || '',
      });
    }
  }, [userData, form]);

  const onSubmit = async (data: FormValues) => {
    try {
      const response = await updateUser({
        body: {
          first_name: data.first_name || null,
          last_name: data.last_name || null,
        },
      }).unwrap();

      if (response.success) {
        toast({
          title: 'Success',
          description: 'Profile updated successfully',
        });
        await refetch();
      } else {
        toast({
          variant: 'error',
          title: 'Error',
          description: 'Failed to update profile',
        });
      }
    } catch (error) {
      console.error('Update error:', error);
      toast({
        variant: 'error',
        title: 'Error',
        description: 'An error occurred while updating your profile',
      });
    }
  };

  const getRoleVariant = (role: string) => {
    switch (role) {
      case 'admin':
        return 'destructive';
      case 'user':
        return 'secondary';
      default:
        return 'outline';
    }
  };

  const getStatusVariant = (status: string) => {
    switch (status) {
      case 'active':
        return 'default';
      case 'inactive':
        return 'secondary';
      case 'deactivated':
      case 'terminated':
        return 'destructive';
      default:
        return 'outline';
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="h-8 bg-muted rounded animate-pulse"></div>
        <Card>
          <CardHeader>
            <div className="h-6 bg-muted rounded animate-pulse"></div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <div className="h-4 bg-muted rounded animate-pulse"></div>
              <div className="h-10 bg-muted rounded animate-pulse"></div>
            </div>
            <div className="space-y-2">
              <div className="h-4 bg-muted rounded animate-pulse"></div>
              <div className="h-10 bg-muted rounded animate-pulse"></div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!userData?.data) {
    return (
      <div className="text-center py-8">
        <p className="text-muted-foreground">Failed to load user data</p>
        <Button onClick={() => refetch()} variant="outline" className="mt-4">
          Retry
        </Button>
      </div>
    );
  }

  const user = userData.data;

  return (
    <div className="space-y-6 w-full max-w-4xl mx-auto">
      <h2 className="text-xl font-semibold px-1">User Settings</h2>

      {/* Account Information Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <UserIcon className="h-5 w-5" />
            Account Information
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Mail className="h-4 w-4" />
                Email
              </Label>
              <div className="flex items-center gap-2">
                <Input value={user.email || ''} disabled className="bg-muted/50 flex-1" />
                {user.email_verified ? (
                  <CheckCircle className="h-4 w-4 text-success flex-shrink-0" />
                ) : (
                  <Badge variant="outline" className="text-xs flex-shrink-0">
                    Unverified
                  </Badge>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Shield className="h-4 w-4" />
                Role & Status
              </Label>
              <div className="flex flex-wrap gap-2">
                <Badge variant={getRoleVariant(user.role)} className="capitalize">
                  {user.role}
                </Badge>
                {user.status && (
                  <Badge variant={getStatusVariant(user.status)} className="capitalize">
                    {user.status}
                  </Badge>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Profile Information Card */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Profile Information</CardTitle>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="first_name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>First Name</FormLabel>
                      <FormControl>
                        <Input placeholder="Enter your first name" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="last_name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Last Name</FormLabel>
                      <FormControl>
                        <Input placeholder="Enter your last name" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="flex justify-end pt-4">
                <Button
                  type="submit"
                  disabled={!form.formState.isDirty || isUpdating}
                  className="min-w-[120px] w-full sm:w-auto"
                >
                  {isUpdating ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    'Save Changes'
                  )}
                </Button>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>

      {/* Account Details Card */}
      <Card className="hidden">
        <CardHeader>
          <CardTitle className="text-lg">Account Details</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 text-sm">
            <div>
              <Label className="text-muted-foreground">User ID</Label>
              <p className="font-mono text-xs bg-muted/50 p-2 rounded border break-all">{user.id}</p>
            </div>

            <div>
              <Label className="text-muted-foreground">Login Type</Label>
              <p className="capitalize">{user.login_type?.replace('_', ' ')}</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
